/* eslint-disable spire/export-styles */
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme from "@insite/mobius/globals/baseTheme";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
// import CustomApplicationState from "@tBrasseler/Store/CustomApplicationState";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const billToId = state.context.session.billToId;
    return {
        isGuest: state.context.session.isGuest,

        billToState: getBillToState(state, billToId || undefined),
    };
};

const enum fields {
    className = "className",
    columns = "columns",
    gap = "gap",
    resizeRows = "resizeRows",
    collapseOnMobile = "collapseOnMobile",
    reverseOrderOnMobile = "reverseOrderOnMobile",
    background = "background",
    image = "image",
    gradientColorLeft = "gradientColorLeft",
    gradientColorMiddle = "gradientColorMiddle",
    gradientColorRight = "gradientColorRight",
    gradientColorTop = "gradientColorTop",
    gradientColorBottom = "gradientColorBottom",
    backgroundColor = "backgroundColor",
    maxWidth = "maxWidth",
    margins = "margins",
    paddings = "paddings",
    hideForGuest = "hideForGuest",
}

interface Props extends WidgetProps {
    fields: {
        [fields.className]: string;
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.resizeRows]: boolean;
        [fields.collapseOnMobile]: boolean;
        [fields.reverseOrderOnMobile]: boolean;
        [fields.background]: "image" | "color" | "horizontal-gradient" | "vertical-gradient";
        [fields.image]: string;
        [fields.gradientColorLeft]: string;
        [fields.gradientColorMiddle]: string;
        [fields.gradientColorRight]: string;
        [fields.gradientColorTop]: string;
        [fields.gradientColorBottom]: string;
        [fields.backgroundColor]: string;
        [fields.maxWidth]: string;
        [fields.margins]: string;
        [fields.paddings]: string;
        [fields.hideForGuest]: boolean;
    };
}

const Container = styled("div")<{
    gap: number;
    collapse: boolean;
    reverse: boolean;
    background: string;
    image: string;
    gradientColorLeft: string;
    gradientColorMiddle: string;
    gradientColorRight: string;
    gradientColorTop: string;
    gradientColorBottom: string;
    backgroundColor: string;
    maxWidth: string;
    margins: string;
    paddings: string;
}>`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    margin: ${p => `${p.margins}`};
    padding: ${p => `${p.paddings}`};
    gap: ${p => `${p.gap}px`};
    ${p => {
        if (p.background === "image") {
            return css`
                background-image: url(${p.image});
                background-size: cover;
            `;
        }
        if (p.background === "horizontal-gradient") {
            return css`
                background: linear-gradient(
                    to right,
                    ${p.gradientColorLeft},
                    ${p.gradientColorMiddle},
                    ${p.gradientColorRight}
                );
            `;
        }
        if (p.background === "vertical-gradient") {
            return css`
                background: linear-gradient(to bottom, ${p.gradientColorTop}, ${p.gradientColorBottom});
            `;
        }
        if (p.background === "color") {
            return css`
                background-color: ${p.backgroundColor};
            `;
        }
    }}
    ${({ theme, reverse }) =>
        breakpointMediaQueries(theme, [
            css`
                flex-direction: ${reverse ? "column-reverse" : "column"};
            `,
            css`
                flex-direction: ${reverse ? "column-reverse" : "column"};
            `,
        ])};
    ${p => {
        if (!p.collapse) {
            return css`
                flex-direction: row !important;
            `;
        }
    }}
    ${({ theme, maxWidth }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return breakpointMediaQueries(theme, [
            css`
                max-width: ${maxWidth || `${maxWidths[1]}px`};
            `,
            css`
                max-width: ${maxWidth || `${maxWidths[1]}px`};
            `,
            css`
                max-width: ${maxWidth || `${maxWidths[2]}px`};
            `,
            css`
                max-width: ${maxWidth || `${maxWidths[3]}px`};
            `,
            css`
                max-width: ${maxWidth || `${maxWidths[4]}px`};
            `,
        ]);
    }}
    &.centerContentRow {
        @media only screen and (max-width: 1440px) {
            padding-left: 15px;
            padding-right: 15px;
        }
    }
    &.orderDetailsContent {
        @media only screen and (max-width: 1440px) {
            padding-left: 15px;
            padding-right: 15px;
        }
        ${({ theme }) => {
            return breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        padding-left: 0;
                        padding-right: 0;
                        .content-pageTitle {
                            padding-left: 15px;
                            padding-right: 15px;
                        }
                    `,
                    null,
                    null,
                ],
                "max",
            );
        }}
    }
    &.billing-shipping-address {
        ${({ theme }) => {
            return breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin-top: 20px;
                    `,
                    null,
                    null,
                ],
                "max",
            );
        }}
    }
`;

const RowItem = styled("div")<{ width: number; resize: boolean }>`
    ${p => {
        if (!p.resize) {
            return css`
                width: ${`${p.width}%`};
            `;
        }
        return css`
            display: inline-flex;
            flex-basis: ${`${p.width}%`};
            flex-grow: 1;
            flex-shrink: 1;
        `;
    }}
    ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                width: 100%;
            `,
            css`
                width: 100%;
            `,
        ])};
`;

type MainProps = Props & ReturnType<typeof mapStateToProps>;

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const CustomRow: React.FunctionComponent<MainProps> = props => {
    const {
        fields: {
            className,
            columns,
            gap,
            resizeRows,
            collapseOnMobile,
            reverseOrderOnMobile,
            background,
            image,
            gradientColorLeft,
            gradientColorMiddle,
            gradientColorRight,
            gradientColorTop,
            gradientColorBottom,
            backgroundColor,
            maxWidth,
            margins,
            paddings,
            hideForGuest,
        },
        id,
        isGuest,

        billToState,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        columnElements.push(
            <RowItem key={x} width={(columns[x] / 12) * 100} resize={resizeRows}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </RowItem>,
        );
    }

    return (
        <Container
            className={className}
            collapse={collapseOnMobile === undefined ? true : collapseOnMobile}
            gap={gap || 0}
            reverse={reverseOrderOnMobile}
            background={background}
            image={image}
            gradientColorLeft={gradientColorLeft}
            gradientColorMiddle={gradientColorMiddle}
            gradientColorRight={gradientColorRight}
            gradientColorTop={gradientColorTop}
            gradientColorBottom={gradientColorBottom}
            backgroundColor={backgroundColor || "transparent"}
            margins={margins}
            paddings={paddings}
            maxWidth={maxWidth}
        >
            {columnElements}
        </Container>
    );
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CustomRow),
    definition: {
        group: "Basic",
        icon: "Row",
        displayName: "CustomRow",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.resizeRows,
                displayName: "Resize rows based on content",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.collapseOnMobile,
                displayName: "Collapse row on mobile",
                defaultValue: true,
                editorTemplate: "CheckboxField",
                fieldType: "General",
            },
            {
                name: fields.reverseOrderOnMobile,
                displayName: "Reverse order on mobile",
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                    { displayName: "HorizontalGradient", value: "horizontal-gradient" },
                    { displayName: "VerticalGradient", value: "vertical-gradient" },
                ],
                defaultValue: "color",
                hideEmptyOption: true,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },

            {
                name: fields.gradientColorLeft,
                displayName: "Gradient Color Left",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: widget => widget.fields.background === "horizontal-gradient",
            },
            {
                name: fields.gradientColorMiddle,
                displayName: "Gradient Color Middle",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: widget => widget.fields.background === "horizontal-gradient",
            },
            {
                name: fields.gradientColorRight,
                displayName: "Gradient Color Right",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: widget => widget.fields.background === "horizontal-gradient",
            },
            {
                name: fields.gradientColorTop,
                displayName: "Gradient Color Top",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: widget => widget.fields.background === "vertical-gradient",
            },
            {
                name: fields.gradientColorBottom,
                displayName: "Gradient Color Bottom",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: widget => widget.fields.background === "vertical-gradient",
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                name: fields.className,
                displayName: "ClassName",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.maxWidth,
                displayName: "Max Width Of The Row",
                defaultValue: "1440px",
                editorTemplate: "TextField",
                fieldType: "General",
            },
            {
                name: fields.margins,
                displayName: "Margins",
                editorTemplate: "TextField",
                defaultValue: "0px auto",
                fieldType: "General",
                placeholder: "top left bottom right",
            },
            {
                name: fields.paddings,
                displayName: "Paddings",
                editorTemplate: "TextField",
                defaultValue: "0px 0px 0px 0px",
                fieldType: "General",
                placeholder: "top left bottom right",
            },
            {
                name: fields.hideForGuest,
                displayName: "Hide For Guest/NWS User",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
